import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceClientService } from '@services/api/device/device-client.service';
import { IDeviceModel } from '@services/api/device/device-model.interface';
import { HardCodedConfigService } from '@services/util/hard-coded-config.service';
import { LogService } from '@services/util/log.service';
import { differenceInMinutes, parseISO, format } from 'date-fns';
import { finalize } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';

interface FlatPresence {
  flatId: number;
  flatNumber: string;
  flatName: string;
  status: string;
  statusChangedAt: string;
  minutesSinceChange: number;
  device: IDeviceModel; // Full device object for debugging
}

interface FloorPresence {
  floorId: number;
  floorName: string;
  level: number;
  flats: FlatPresence[];
}

interface BuildingPresence {
  buildingId: number;
  buildingName: string;
  floors: FloorPresence[];
}

@Component({
  selector: 'app-presence-overview',
  standalone: true,
  imports: [CommonModule, TranslateModule, DialogModule, ButtonModule],
  template: `
    <div class="compact-card">
      <!-- Header -->
      <div class="card-header mb-6">
        <h1 class="text-2xl font-bold">{{ 'presence.overview.title' | translate }}</h1>
        <p class="mt-1 text-base text-gray-500 dark:text-gray-300">{{ 'presence.overview.subtitle' | translate }}</p>
      </div>

      @if (loading()) {
        <div class="flex items-center justify-center py-8">
          <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
          <span class="ml-3 text-lg">{{ 'common.loading' | translate }}</span>
        </div>
      } @else if (buildings().length === 0) {
        <div class="p-4 text-center">
          <p>{{ 'presence.overview.noDevices' | translate }}</p>
        </div>
      } @else {
        <!-- Side-by-side building cards -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          @for (building of buildings(); track building.buildingId) {
            <div class="rounded-lg border bg-white dark:bg-gray-800 dark:border-gray-700 p-6">
              <h2 class="mb-4 text-lg font-semibold">{{ building.buildingName }}</h2>

              <!-- Floor rows -->
              @for (floor of building.floors; track floor.floorId) {
                <div class="mb-4 flex items-center gap-3">
                  <div class="w-16 shrink-0 text-sm text-gray-600 dark:text-gray-300">{{ floor.floorName }}</div>
                  <div class="flex flex-wrap gap-2">
                    @for (flat of floor.flats; track flat.flatId) {
                      <button
                        type="button"
                        class="h-10 w-10 rounded-full transition-all hover:scale-110 hover:shadow-md flex items-center justify-center text-xs font-medium"
                        [class.bg-primary]="isPresenceDetected(flat.status)"
                        [class.text-white]="isPresenceDetected(flat.status)"
                        [class.bg-gray-300]="!isPresenceDetected(flat.status)"
                        [class.text-gray-700]="!isPresenceDetected(flat.status)"
                        (click)="openModal(flat, building.buildingName)"
                        [attr.aria-label]="'Lakás ' + flat.flatNumber"
                      >
                        {{ flat.flatNumber }}
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>

        <!-- Legend -->
        <div class="mt-6 flex items-center px-6 gap-6 text-sm">
          <div class="flex items-center gap-2">
            <div class="h-4 w-4 rounded-full bg-primary"></div>
            <span>{{ 'presence.overview.legend.occupied' | translate }}</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="h-4 w-4 rounded-full bg-gray-300"></div>
            <span>{{ 'presence.overview.legend.empty' | translate }}</span>
          </div>
        </div>
      }
    </div>

    <!-- Detail Modal -->
    <p-dialog
      [(visible)]="modalVisible"
      [modal]="true"
      [closable]="true"
      [style]="{ width: '400px' }"
      [dismissableMask]="true"
    >
      <ng-template pTemplate="header">
        <div class="text-base font-semibold">
          {{ selectedBuilding() }} › {{ 'presence.overview.modal.apartmentLabel' | translate }} {{ selectedFlat()?.flatNumber }}
        </div>
      </ng-template>

      @if (selectedFlat()) {
        <div class="space-y-4">
          <!-- Status Banner -->
          <div
            class="rounded-lg p-3"
            [class.bg-primary-50]="isPresenceDetected(selectedFlat()!.status)"
            [class.text-primary-800]="isPresenceDetected(selectedFlat()!.status)"
            [class.bg-gray-100]="!isPresenceDetected(selectedFlat()!.status)"
            [class.text-gray-700]="!isPresenceDetected(selectedFlat()!.status)"
          >
            <div class="flex items-center gap-2">
              <div
                class="h-3 w-3 rounded-full"
                [class.bg-primary]="isPresenceDetected(selectedFlat()!.status)"
                [class.bg-gray-400]="!isPresenceDetected(selectedFlat()!.status)"
              ></div>
              <span class="font-medium">
                {{ isPresenceDetected(selectedFlat()!.status) ? ('presence.overview.modal.occupied' | translate) : ('presence.overview.modal.empty' | translate) }}
              </span>
            </div>
          </div>

          <!-- Time Info -->
          <div class="space-y-2 text-sm">
            @if (!isPresenceDetected(selectedFlat()!.status)) {
              <div class="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <i class="pi pi-clock"></i>
                <span>
                  {{ 'presence.overview.modal.emptyFor' | translate }}: {{ getTimeSinceChange(selectedFlat()!.minutesSinceChange) }}
                </span>
              </div>
            }
            <div class="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <i class="pi pi-calendar"></i>
              <span>
                {{ 'presence.overview.modal.lastChanged' | translate }}: {{ formatDateTime(selectedFlat()!.statusChangedAt) }}
                @if (isPresenceDetected(selectedFlat()!.status)) {
                  ({{ getTimeSinceChange(selectedFlat()!.minutesSinceChange) }})
                }
              </span>
            </div>
          </div>

          <!-- Notice -->
          <div class="rounded bg-gray-50 p-3 text-xs text-gray-500">
            {{ 'presence.overview.modal.noHistory' | translate }}
          </div>
        </div>
      }
    </p-dialog>
  `,
  styles: []
})
export class PresenceOverviewComponent implements OnInit {
  private readonly deviceClientService = inject(DeviceClientService);
  private readonly config = inject(HardCodedConfigService);
  private readonly log = inject(LogService);
  private readonly translate = inject(TranslateService);

  loading = signal<boolean>(false);
  buildings = signal<BuildingPresence[]>([]);
  modalVisible = signal<boolean>(false);
  selectedFlat = signal<FlatPresence | null>(null);
  selectedBuilding = signal<string>('');

  ngOnInit(): void {
    this.loadPresenceData();
  }

  /**
   * Load all presence detector devices and organize by building/floor
   */
  private loadPresenceData(): void {
    this.loading.set(true);

    this.deviceClientService
      .getDevicesByType(this.config.DEVICE_TYPE_PRESENCE_DETECTOR_ID)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (devices: IDeviceModel[]) => {
          this.log.info('Presence devices loaded via device type endpoint', devices.length);
          
          // Log device types to verify filtering
          const deviceTypes = devices.map(d => ({
            deviceId: d.id,
            deviceTypeId: d.deviceType?.id,
            deviceTypeName: d.deviceType?.name,
            flatNumber: d.flat?.flat_number
          }));
          this.log.info('Device types in response:', deviceTypes);
          
          // Count unique device types
          const uniqueTypes = [...new Set(devices.map(d => d.deviceType?.id))];
          this.log.info('Unique device type IDs in response:', uniqueTypes);
          
          this.organizeDevicesByBuilding(devices);
        },
        error: (error) => {
          this.log.error('Failed to load presence devices', error);
          this.buildings.set([]);
        }
      });
  }

  /**
   * Organize devices into building → floor → flat structure
   */
  private organizeDevicesByBuilding(devices: IDeviceModel[]): void {
    const buildingMap = new Map<number, BuildingPresence>();

    devices.forEach((device) => {
      if (!device.flat || !device.flat.building || !device.flat.floor) {
        return;
      }

      const buildingId = device.flat.building.id;
      const floorId = device.flat.floor.id;

      // Get or create building
      if (!buildingMap.has(buildingId)) {
        buildingMap.set(buildingId, {
          buildingId,
          buildingName: device.flat.building.name || 'Unknown',
          floors: []
        });
      }

      const building = buildingMap.get(buildingId)!;

      // Get or create floor
      let floor = building.floors.find((f) => f.floorId === floorId);
      if (!floor) {
        floor = {
          floorId,
          floorName: device.flat.floor.name || 'Unknown',
          level: device.flat.floor.level || 0,
          flats: []
        };
        building.floors.push(floor);
      }

      // Add flat
      const minutesSinceChange = device.status_changed_at
        ? differenceInMinutes(new Date(), parseISO(device.status_changed_at))
        : 999999;

      floor.flats.push({
        flatId: device.flat.id,
        flatNumber: device.flat.flat_number || '?',
        flatName: device.flat.name || '',
        status: device.status || '0',
        statusChangedAt: device.status_changed_at || '',
        minutesSinceChange,
        device
      });
    });

    // Sort buildings by name
    const buildings = Array.from(buildingMap.values()).sort((a, b) =>
      a.buildingName.localeCompare(b.buildingName)
    );

    // Sort floors by level within each building (descending - higher floors first)
    buildings.forEach((building) => {
      building.floors.sort((a, b) => b.level - a.level);

      // Sort flats by flat number within each floor
      building.floors.forEach((floor) => {
        floor.flats.sort((a, b) => {
          const aNum = parseFloat(a.flatNumber) || 0;
          const bNum = parseFloat(b.flatNumber) || 0;
          return aNum - bNum;
        });
      });
    });

    this.buildings.set(buildings);
    this.log.debug('Organized buildings', buildings);
  }

  /**
   * Check if status indicates presence
   */
  isPresenceDetected(status: string): boolean {
    const presenceStatuses = ['occupied', 'present', 'detected', '1', 'true', 'on'];
    return presenceStatuses.includes(status.toLowerCase());
  }

  /**
   * Open modal with flat details
   */
  openModal(flat: FlatPresence, buildingName: string): void {
    this.selectedFlat.set(flat);
    this.selectedBuilding.set(buildingName);
    this.modalVisible.set(true);
  }

  /**
   * Format time since change in human readable format
   */
  getTimeSinceChange(minutes: number): string {
    if (minutes < 1) return this.translate.instant('presence.lessThan1MinuteAgo');
    if (minutes < 60) return this.translate.instant('presence.minutesAgo', { count: Math.floor(minutes) });
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return this.translate.instant('presence.1HourAgo');
    if (hours < 24) return this.translate.instant('presence.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    return this.translate.instant('presence.daysAgo', { count: days });
  }

  /**
   * Format datetime for display
   */
  formatDateTime(isoString: string): string {
    if (!isoString) {
      return 'N/A';
    }
    return format(parseISO(isoString), 'yyyy. MM. dd. HH:mm:ss');
  }
}
